import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type NotificationType =
  | 'favorite_added'
  | 'favorite_removed'
  | 'model_uploaded'
  | 'model_deleted'
  | 'capture_saved'
  | 'capture_deleted'
  | 'profile_updated'
  | 'welcome'
  | 'ar_session'
  | 'system';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  icon?: string;
  modelId?: string;
  modelName?: string;
  thumbnailUrl?: string;
  read: boolean;
  createdAt: Date;
}

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  isDrawerOpen: boolean;

  // Actions
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: string) => void;
  clearAll: () => void;
  setDrawerOpen: (open: boolean) => void;

  // Convenience methods for common notifications
  notifyFavoriteAdded: (modelName: string, modelId: string, thumbnailUrl?: string) => void;
  notifyFavoriteRemoved: (modelName: string) => void;
  notifyModelUploaded: (modelName: string, modelId: string) => void;
  notifyModelDeleted: (modelName: string) => void;
  notifyCaptureSaved: (type: 'photo' | 'video', modelName?: string) => void;
  notifyARSession: (modelName: string, duration: number) => void;
  notifyWelcome: (userName: string) => void;
  notifyProfileUpdated: () => void;
}

const generateId = () => `notif_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set, get) => ({
      notifications: [],
      unreadCount: 0,
      isDrawerOpen: false,

      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: generateId(),
          read: false,
          createdAt: new Date(),
        };

        set((state) => ({
          notifications: [newNotification, ...state.notifications].slice(0, 100), // Keep max 100
          unreadCount: state.unreadCount + 1,
        }));
      },

      markAsRead: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (!notification || notification.read) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      deleteNotification: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          const wasUnread = notification && !notification.read;

          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        });
      },

      clearAll: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      setDrawerOpen: (open) => {
        set({ isDrawerOpen: open });
      },

      // Convenience methods
      notifyFavoriteAdded: (modelName, modelId, thumbnailUrl) => {
        get().addNotification({
          type: 'favorite_added',
          title: 'Added to Favorites',
          message: `${modelName} has been added to your favorites collection.`,
          modelId,
          modelName,
          thumbnailUrl,
        });
      },

      notifyFavoriteRemoved: (modelName) => {
        get().addNotification({
          type: 'favorite_removed',
          title: 'Removed from Favorites',
          message: `${modelName} has been removed from your favorites.`,
          modelName,
        });
      },

      notifyModelUploaded: (modelName, modelId) => {
        get().addNotification({
          type: 'model_uploaded',
          title: 'Model Uploaded',
          message: `Your model "${modelName}" has been successfully uploaded and is ready to view in AR.`,
          modelId,
          modelName,
        });
      },

      notifyModelDeleted: (modelName) => {
        get().addNotification({
          type: 'model_deleted',
          title: 'Model Deleted',
          message: `${modelName} has been permanently deleted from your library.`,
          modelName,
        });
      },

      notifyCaptureSaved: (type, modelName) => {
        get().addNotification({
          type: 'capture_saved',
          title: type === 'photo' ? 'Photo Captured' : 'Video Recorded',
          message: modelName
            ? `Your AR ${type} of "${modelName}" has been saved to your captures.`
            : `Your AR ${type} has been saved to your captures.`,
          modelName,
        });
      },

      notifyARSession: (modelName, duration) => {
        const minutes = Math.round(duration / 60);
        get().addNotification({
          type: 'ar_session',
          title: 'AR Session Complete',
          message: `You viewed "${modelName}" in AR for ${minutes} minute${minutes !== 1 ? 's' : ''}.`,
          modelName,
        });
      },

      notifyWelcome: (userName) => {
        get().addNotification({
          type: 'welcome',
          title: 'Welcome to VisionAR!',
          message: `Hey ${userName}! Start exploring 3D models and view them in augmented reality.`,
        });
      },

      notifyProfileUpdated: () => {
        get().addNotification({
          type: 'profile_updated',
          title: 'Profile Updated',
          message: 'Your profile has been successfully updated.',
        });
      },
    }),
    {
      name: 'visionar-notifications',
      partialize: (state) => ({
        notifications: state.notifications,
        unreadCount: state.unreadCount,
      }),
    }
  )
);
